import { type FC, type ReactNode } from 'react'
import { Sticky } from '../../../components/Sticky'
import { type BookingClient, type BookingInvitee, type Location, type MeetingMode, type SchedulingEventType } from '../../../models/Scheduling'
import { type LocationInput } from '../../../store/scheduling/dto'
import { CalendarInvitePreview } from './CalendarInvitePreview'
import { type TimeSelectionMode } from './form/SetTimeSection'
import { type BookingSlot } from './form/timeSlots'
import { InvitationEmailPreview } from './InvitationEmailPreview'

interface SchedulePreviewColumnProps {
  eventType?: SchedulingEventType
  mode: TimeSelectionMode
  emptyState: ReactNode
  ownerName: string
  invitees: BookingInvitee[]
  meetingMode: MeetingMode
  slot: BookingSlot | null
  title: string
  timezoneLabel: string
  location: Location | LocationInput | null
  note: string
  onNoteChange: (note: string) => void
  client?: BookingClient
  onEditEmail: () => void
  onCopyEmail: () => void
  copyEmailDisabled?: boolean
}

// Right column shared by the schedule and reschedule flows: an empty state until an event type is
// known, then the calendar invite (specific time) or the invitation email (client chooses).
export const SchedulePreviewColumn: FC<SchedulePreviewColumnProps> = ({
  eventType,
  mode,
  emptyState,
  ownerName,
  invitees,
  meetingMode,
  slot,
  title,
  timezoneLabel,
  location,
  note,
  onNoteChange,
  client,
  onEditEmail,
  onCopyEmail,
  copyEmailDisabled = false,
}) => {
  if (!eventType) {
    return <>{emptyState}</>
  }
  if (mode === 'specific-time') {
    return (
      <Sticky mode='top'>
        <CalendarInvitePreview
          eventType={eventType}
          ownerName={ownerName}
          invitees={invitees}
          meetingMode={meetingMode}
          slot={slot}
          title={title}
          timezoneLabel={timezoneLabel}
          location={location}
          note={note}
          onNoteChange={onNoteChange}
        />
      </Sticky>
    )
  }
  return (
    <Sticky mode='top'>
      <InvitationEmailPreview
        eventType={eventType}
        client={client}
        ownerName={ownerName}
        onEdit={onEditEmail}
        onCopyEmail={onCopyEmail}
        copyDisabled={copyEmailDisabled}
      />
    </Sticky>
  )
}
